// Internal memoized `Intl.DateTimeFormat` factory — not part of the public surface.
//
// Constructing an `Intl.DateTimeFormat` is relatively expensive, and these
// formatters get called per-row in countdowns and lists once the package is
// adopted (PR3/PR4/PR6). `Intl` formatter instances are immutable and safe to
// reuse, so we memoize by (locale, options). This keeps the optimization in one
// place instead of leaving an implicit "every consumer must memoize" contract
// that would inevitably drift. Pure from the caller's view: same inputs → same
// instance, no observable side effect.

const cache = new Map<string, Intl.DateTimeFormat>();

/** Return a cached `Intl.DateTimeFormat` for the given locale + options. */
export function getDateTimeFormat(
    locale: string,
    opts: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
    // Locale tokens contain no spaces and the JSON starts with `{`, so a single
    // space is an unambiguous key separator. Distinct option orderings just
    // produce distinct cache entries — correctness is preserved either way.
    const key = `${locale} ${JSON.stringify(opts)}`;
    let fmt = cache.get(key);
    if (!fmt) {
        fmt = new Intl.DateTimeFormat(locale, opts);
        cache.set(key, fmt);
    }
    return fmt;
}
